import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'react-toastify';
import { MdSearch } from 'react-icons/md';
import ModalForm from '../../../components/modal-form';
import { Campo } from '../../../services/tipos';
import { TipoFormulario } from '../../../services/constantes';
import {
  LocacaoMonitoravel,
  geocodificarCidade,
  geocodificarEndereco,
  geocodificarTexto,
  salvarLocalizacaoLocatario,
} from '../api';

interface Props {
  locacao: LocacaoMonitoravel | null;
  idEmpresa: string;
  onClose: () => void;
  onSalvo: (idLocatario: string, lat: number, lng: number) => void;
}

interface EntidadeLocalizacao {
  endereco: string;
  latitude: number | null;
  longitude: number | null;
  origem: 'busca' | 'mapa';
}

const CENTRO_BRASIL: [number, number] = [-15.78, -47.93];

const PINO = L.divIcon({
  className: '',
  html: `<svg width="30" height="40" viewBox="0 0 34 46" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 1C8.2 1 1 8.1 1 16.8 1 28.6 17 45 17 45s16-16.4 16-28.2C33 8.1 25.8 1 17 1z"
      fill="#1F467E" stroke="#fff" stroke-width="2"/>
    <circle cx="17" cy="16.5" r="6" fill="#fff"/>
  </svg>`,
  iconSize: [30, 40],
  iconAnchor: [15, 40],
});

const CliqueNoMapa: React.FC<{ onPick: (lat: number, lng: number) => void }> = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
};

const cor = { borda: '#e0e5ee', texto: '#1f2937', suave: '#64748b' };

interface MapaProps {
  locacao: LocacaoMonitoravel;
  entidade: EntidadeLocalizacao;
  handleInputChange: (campo: string, valor: string | number | boolean) => void;
}

/** Campo do mapa: busca o endereço digitado, coloca o pino e deixa clicar ou arrastar para ajustar. */
const MapaLocalizacao: React.FC<MapaProps> = ({ locacao, entidade, handleInputChange }) => {
  const [aviso, setAviso] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [centro, setCentro] = useState<[number, number]>(
    locacao.localizado && locacao.latitude != null && locacao.longitude != null ? [locacao.latitude, locacao.longitude] : CENTRO_BRASIL,
  );
  const [zoom, setZoom] = useState(locacao.localizado ? 16 : 4);
  const mapRef = useRef<L.Map | null>(null);

  const temPonto = entidade.latitude != null && entidade.longitude != null;

  const posicionar = useCallback((lat: number, lng: number, z: number) => {
    setCentro([lat, lng]);
    setZoom(z);
    mapRef.current?.setView([lat, lng], z);
  }, []);

  const marcar = useCallback((lat: number, lng: number, origem: 'busca' | 'mapa') => {
    handleInputChange('latitude', lat);
    handleInputChange('longitude', lng);
    handleInputChange('origem', origem);
  }, [handleInputChange]);

  const semResultado = useCallback(async () => {
    const cidade = await geocodificarCidade({ cidade: locacao.cidade, estado: locacao.estado });
    posicionar(cidade?.lat ?? CENTRO_BRASIL[0], cidade?.lng ?? CENTRO_BRASIL[1], cidade ? 12 : 4);
    setAviso('Não encontrei o endereço. Tente escrever de outro jeito ou clique no mapa onde o locatário mora.');
  }, [locacao.cidade, locacao.estado, posicionar]);

  useEffect(() => {
    if (locacao.localizado) return;
    let ativo = true;
    setBuscando(true);
    geocodificarEndereco(locacao)
      .then(async (achado) => {
        if (!ativo) return;
        if (achado) {
          marcar(achado.lat, achado.lng, 'busca');
          posicionar(achado.lat, achado.lng, 16);
          setAviso('Encontrei este ponto para o endereço. Confira e, se precisar, arraste o pino ou clique no lugar certo.');
        } else {
          await semResultado();
        }
      })
      .finally(() => { if (ativo) setBuscando(false); });
    return () => { ativo = false; };
    // Só na abertura: a busca pelo texto digitado é feita pelo botão.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locacao.idLocatario]);

  const buscar = useCallback(async () => {
    const texto = (entidade.endereco || '').trim();
    if (!texto) return;
    setBuscando(true);
    try {
      const achado = await geocodificarTexto(texto);
      if (achado) {
        marcar(achado.lat, achado.lng, 'busca');
        posicionar(achado.lat, achado.lng, 16);
        setAviso('Confira o ponto e, se precisar, arraste o pino ou clique no lugar certo.');
      } else {
        await semResultado();
      }
    } finally {
      setBuscando(false);
    }
  }, [entidade.endereco, marcar, posicionar, semResultado]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={buscar}
          disabled={buscando || !(entidade.endereco || '').trim()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <MdSearch size={16} /> {buscando ? 'Buscando...' : 'Buscar no mapa'}
        </button>
        {aviso && <div style={{ fontSize: 12.5, color: cor.texto }}>{aviso}</div>}
      </div>
      <div style={{ height: 380, borderRadius: 8, overflow: 'hidden', border: `1px solid ${cor.borda}` }}>
        <MapContainer
          center={centro}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          whenCreated={(m) => { mapRef.current = m; setTimeout(() => m.invalidateSize(), 300); }}
        >
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <CliqueNoMapa onPick={(lat, lng) => { marcar(lat, lng, 'mapa'); setAviso(''); }} />
          {temPonto && (
            <Marker
              position={[entidade.latitude!, entidade.longitude!]}
              icon={PINO}
              draggable
              eventHandlers={{
                dragend: (e: any) => {
                  const { lat, lng } = e.target.getLatLng();
                  marcar(lat, lng, 'mapa');
                },
              }}
            />
          )}
        </MapContainer>
      </div>
      <div style={{ fontSize: 12, color: cor.suave, marginTop: 6 }}>
        Clique no mapa ou arraste o pino para ajustar. O ponto fica salvo no cadastro do locatário e vale para as próximas locações dele.
      </div>
    </div>
  );
};

/**
 * Localiza o endereço do locatário no mapa, num ModalForm próprio: endereço digitado, busca e pino.
 * Abre a partir da lista de locações monitoradas do evento de pernoite.
 */
const ModalLocalizarEndereco: React.FC<Props> = ({ locacao, idEmpresa, onClose, onSalvo }) => {
  const [salvando, setSalvando] = useState(false);

  const entidade = useMemo<EntidadeLocalizacao | undefined>(() => (locacao ? {
    endereco: locacao.endereco || '',
    latitude: locacao.localizado ? locacao.latitude ?? null : null,
    longitude: locacao.localizado ? locacao.longitude ?? null : null,
    origem: 'mapa',
  } : undefined), [locacao]);

  const campos = useMemo<Campo[]>(() => {
    if (!locacao) return [];
    return [
      {
        label: 'Endereço',
        nome: 'endereco',
        tipo: 'text',
        required: false,
        largura: 12,
        coluna: 1,
        linha: 1,
        ajudinha: { texto: 'Escreva o endereço como quiser (rua, número, bairro, cidade) e clique em Buscar no mapa.' },
      },
      {
        label: 'Ponto no mapa',
        nome: 'mapa',
        tipo: 'custom',
        required: false,
        largura: 12,
        coluna: 1,
        linha: 2,
        component: (props: any) => React.createElement(MapaLocalizacao, {
          locacao,
          entidade: props.entidade,
          handleInputChange: props.handleInputChange,
        }),
      },
    ];
  }, [locacao]);

  const confirmar = useCallback(async (dados: EntidadeLocalizacao) => {
    if (!locacao) return;
    if (dados.latitude == null || dados.longitude == null) {
      toast.warning('Marque o ponto no mapa antes de confirmar.');
      return;
    }
    setSalvando(true);
    try {
      await salvarLocalizacaoLocatario(idEmpresa, locacao.idLocatario, dados.latitude, dados.longitude, dados.origem || 'mapa');
      toast.success('Endereço localizado.');
      onSalvo(locacao.idLocatario, dados.latitude, dados.longitude);
    } catch (err: any) {
      toast.error(err?.response?.data?.Mensagem || err?.response?.data?.mensagem || 'Não foi possível salvar o ponto.');
    } finally {
      setSalvando(false);
    }
  }, [idEmpresa, locacao, onSalvo]);

  return (
    <ModalForm
      show={!!locacao}
      modo={TipoFormulario.tpEdicao}
      tamanho="lg"
      titulo={locacao ? `Localizar endereço · ${locacao.nomeLocatario} · ${locacao.placa}` : 'Localizar endereço'}
      tituloBotaoOk="Confirmar endereço"
      campos={campos}
      carregando={false}
      isSaving={salvando}
      entidadeSelecionada={entidade}
      onHide={onClose}
      onCancel={onClose}
      onSave={confirmar}
      apiConfig={{
        endpoint: 'GestaoRiscoLocalizacaoLocatario',
        idTenentCampo: 'idEmpresa',
        requisicaoBemSucedida: () => { },
        tratarErro: () => toast.error('Não foi possível salvar o ponto.'),
      }}
    />
  );
};

export default ModalLocalizarEndereco;
